import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';

interface AttendanceRecord {
  attendance_date: string;
  status: 'present' | 'absent' | 'late' | 'leave';
  notes?: string;
}

interface AttendanceCalendarProps {
  records: AttendanceRecord[];
}

const statusColors: Record<string, string> = {
  present: 'bg-lime-500',
  absent: 'bg-rose-500',
  late: 'bg-amber-500',
  leave: 'bg-sky-500',
};

const statusLabels: Record<string, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  leave: 'Leave',
};

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function formatDateString(year: number, month: number, day: number) {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function ModuleAttendanceCalendar({ records }: AttendanceCalendarProps) {
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const recordsByDate = useMemo(() => {
    const map: Record<string, AttendanceRecord> = {};
    records.forEach((record) => {
      if (!map[record.attendance_date]) map[record.attendance_date] = record;
    });
    return map;
  }, [records]);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const todayStr = formatDateString(now.getFullYear(), now.getMonth(), now.getDate());

  const goToPrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const selectedRecord = selectedDate ? recordsByDate[selectedDate] : null;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-light-border dark:border-dark-border p-3">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={goToPrevMonth} className="p-1.5 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated transition-colors">
            <ChevronLeft className="w-4 h-4 text-light-text-muted" />
          </button>
          <p className="text-sm font-semibold text-light-text dark:text-dark-text">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </p>
          <button onClick={goToNextMonth} className="p-1.5 rounded-lg hover:bg-light-elevated dark:hover:bg-dark-elevated transition-colors">
            <ChevronRight className="w-4 h-4 text-light-text-muted" />
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {DAY_NAMES.map((day) => (
            <div key={day} className="text-center text-[9px] font-medium text-light-text-muted py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-0.5">
          {/* Empty cells for days before the 1st */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = formatDateString(viewYear, viewMonth, day);
            const record = recordsByDate[dateStr];
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;

            return (
              <button
                key={day}
                onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                className={cn(
                  'aspect-square rounded-lg flex items-center justify-center text-xs font-medium transition-all relative',
                  isSelected && 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-500/10',
                  isToday && !isSelected && 'ring-1 ring-purple-300 dark:ring-purple-700',
                  record && !isSelected && 'text-white',
                  !record && !isSelected && 'text-light-text dark:text-dark-text hover:bg-light-elevated dark:hover:bg-dark-elevated',
                )}
                style={record && !isSelected ? { backgroundColor: record.status === 'present' ? '#22c55e' : record.status === 'absent' ? '#f43f5e' : record.status === 'late' ? '#f59e0b' : '#0ea5e9' } : undefined}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-2 pt-2 border-t border-light-border dark:border-dark-border flex flex-wrap gap-3 justify-center">
          {Object.entries(statusLabels).map(([status, label]) => (
            <div key={status} className="flex items-center gap-1">
              <div className={cn('w-2 h-2 rounded-full', statusColors[status])} />
              <span className="text-[10px] text-light-text-muted">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {selectedRecord && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-light-border dark:border-dark-border bg-white dark:bg-dark-surface p-3 flex items-center justify-between"
        >
          <div>
            <p className="font-semibold text-sm">{selectedDate}</p>
            <p className={cn(
              'text-xs capitalize font-medium',
              selectedRecord.status === 'present' && 'text-lime-600',
              selectedRecord.status === 'absent' && 'text-rose-600',
              selectedRecord.status === 'late' && 'text-amber-600',
              selectedRecord.status === 'leave' && 'text-sky-600'
            )}>
              {selectedRecord.status}
              {selectedRecord.notes && ` — ${selectedRecord.notes}`}
            </p>
          </div>
          <div className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center',
            selectedRecord.status === 'present' && 'bg-lime-100 dark:bg-lime-500/20 text-lime-600',
            selectedRecord.status === 'absent' && 'bg-rose-100 dark:bg-rose-500/20 text-rose-600',
            selectedRecord.status === 'late' && 'bg-amber-100 dark:bg-amber-500/20 text-amber-600',
            selectedRecord.status === 'leave' && 'bg-sky-100 dark:bg-sky-500/20 text-sky-600'
          )}>
            {selectedRecord.status === 'present' ? <CheckCircle className="w-4 h-4" /> :
             selectedRecord.status === 'absent' ? <XCircle className="w-4 h-4" /> :
             <Clock className="w-4 h-4" />}
          </div>
        </motion.div>
      )}
    </div>
  );
}
